import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { FluentProvider, webDarkTheme } from '@fluentui/react-components'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import App from './App'
import { msalConfig } from './authConfig'
import { apiFetchJson, getDevAuthToken, setMsalInstance } from './lib/apiFetch'
import { MeContext } from './lib/me'
import type { MeResponse } from './types'
import './index.css'

const msalInstance = new PublicClientApplication(msalConfig)
setMsalInstance(msalInstance)

async function bootstrap() {
  await msalInstance.initialize()

  // Dev impersonation (see docs/DEV_AUTH.md): when a token is present in localStorage, skip the
  // interactive MSAL sign-in entirely so the deployed app can be opened from a plain browser as a
  // fixed admin. MsalProvider still wraps the app (Sidebar's useMsal needs it), it just has no
  // signed-in account to show. Only works when the API has DEV_AUTH_ENABLED=true.
  const devToken = getDevAuthToken()
  if (!devToken) {
    await msalInstance.handleRedirectPromise()

    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      await msalInstance.loginRedirect()
      return // page is navigating away
    }
    msalInstance.setActiveAccount(accounts[0])
  }

  // Identity + effective access for UI gating (server enforces the same rules independently).
  let me: MeResponse
  try {
    me = await apiFetchJson<MeResponse>('/api/me')
  } catch (e) {
    document.getElementById('root')!.textContent = `Sign-in failed: ${String(e)}`
    return
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <MeContext.Provider value={me}>
          {/* height (not minHeight) so App.tsx's height: '100%' has an actual ancestor height to
              resolve against — a min-height doesn't establish a percentage-height containing
              block, so App's div was falling back to content-height and stretching the sidebar
              to match tall page content instead of staying pinned to the viewport. */}
          <FluentProvider theme={webDarkTheme} style={{ height: '100%' }}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </FluentProvider>
        </MeContext.Provider>
      </MsalProvider>
    </React.StrictMode>,
  )
}

bootstrap().catch(e => {
  document.getElementById('root')!.textContent = `Sign-in failed: ${String(e)}`
})
